import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useState, useEffect } from "react";
import Axios from "axios";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import { CSVLink } from "react-csv";
import logUserActivity from "../utils/logUserActivity";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import "bootstrap/dist/css/bootstrap.min.css";
import "./reorder.css";

const tdStyle = {
  padding: "8px",
  fontSize: "13px",
  verticalAlign: "middle",
  wordBreak: "break-word",
  whiteSpace: "nowrap",
};

function Reorder() {
  const [skuData, setSkuData] = useState([]);
  const [reorderEntries, setReorderEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [shelfData, setShelfData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [formValues, setFormValues] = useState({
    selectedSKU: "",
    amazonWarehouse: "",
    flipkartWarehouse: "",
    amazon20DaysLastSale: "",
    flipkart20DaysLastSale: "",
    yetToCome: "",
  });

  useEffect(() => {
    fetchSKUInfo();
    fetchReorderData();
  }, []);

  const fetchSKUInfo = () => {
    Axios.get(`${INVENTORY_API_BASE_URL}/getSelectedSKUInfo`)
      .then((res) => setSkuData(res.data))
      .catch((err) => console.error("Error fetching SKU info:", err));
  };

  const fetchReorderData = () => {
    Axios.get(`${INVENTORY_API_BASE_URL}/getAllReorderData`)
      .then((res) => setReorderEntries(res.data))
      .catch((err) => console.error("Error fetching reorder data:", err));
  };

  useEffect(() => {
    Axios.get(`${INVENTORY_API_BASE_URL}/getCreateshelf`)
      .then((res) => {
        const processedData = res.data.reduce((acc, item) => {
          const existing = acc.find((x) => x.sku === item.sku);
          if (existing) {
            existing.totalQty += item.presentquantity;
            existing.shelfDetails.push({
              shelfno: item.shelfno,
              presentquantity: item.presentquantity,
            });
          } else {
            acc.push({
              sku: item.sku,
              totalQty: item.presentquantity,
              shelfDetails: [
                {
                  shelfno: item.shelfno,
                  presentquantity: item.presentquantity,
                },
              ],
            });
          }
          return acc;
        }, []);
        setShelfData(processedData);
      })
      .catch((err) => console.error("Error fetching shelf data:", err));
  }, []);

  const getShelfInfoBySKU = (sku) => {
    const match = shelfData.find((item) => item.sku === sku);
    if (!match) return { total: "N.F", tooltip: "No shelf data found" };

    const tooltip = match.shelfDetails
      .map((d) => `${d.shelfno} ➝ ${d.presentquantity}`)
      .join("\n");

    return { total: match.totalQty, tooltip };
  };

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setFormValues({
      selectedSKU: "",
      amazonWarehouse: "",
      flipkartWarehouse: "",
      amazon20DaysLastSale: "",
      flipkart20DaysLastSale: "",
      yetToCome: "",
    });
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const oldEntry = getLatestReorderForSKU(formValues.selectedSKU);

    try {
      const res = await Axios.post(
        `${INVENTORY_API_BASE_URL}/saveReorderForm`,
        formValues
      );

      await logUserActivity("UPDATE_REORDER_ENTRY", {
        sku: formValues.selectedSKU,
        oldValues: oldEntry || "No previous entry",
        newValues: formValues,
      });

      fetchReorderData();
      handleClose();
    } catch (err) {
      console.error("Error saving form:", err);
    }
  };

  const getLatestReorderForSKU = (sku) => {
    const matches = reorderEntries.filter((entry) => entry.selectedSKU === sku);
    if (matches.length === 0) return null;
    return matches.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    )[0];
  };

  return (
    <div className="reorder-container">
      <main className="reorder-content">
      <header className="reorder-hero">
        <div className="reorder-hero-copy">
          <span className="reorder-eyebrow">INVENTORY / PLANNING</span>
          <h1>Reorder management</h1>
          <p>Review stock coverage and update marketplace replenishment details.</p>
        </div>
        <div className="reorder-hero-metrics" aria-label="Reorder summary">
          <div><span>Tracked SKUs</span><strong>{skuData.length.toLocaleString()}</strong></div>
          <div><span>Saved entries</span><strong>{reorderEntries.length.toLocaleString()}</strong></div>
        </div>
      </header>

      <div className="reorder-toolbar">
        <Button variant="primary" className="reorder-add-button" onClick={handleShow}>
          Add Re-Order
        </Button>
        <Form.Control
          className="reorder-search"
          aria-label="Search by SKU or title"
          type="text"
          placeholder="Search by SKU or Title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
        />

        <CSVLink
          className="reorder-export-button"
          data={skuData.map((item, index) => {
            const reorder = getLatestReorderForSKU(item.sku);
            const pw = getShelfInfoBySKU(item.sku).total;
            const amazon = Number(reorder?.amazonWarehouse ?? 0);
            const flipkart = Number(reorder?.flipkartWarehouse ?? 0);
            const totalStock = (!isNaN(pw) ? pw : 0) + amazon + flipkart;
            const fk20 = Number(reorder?.flipkart20DaysLastSale ?? 0);
            const amz20 = Number(reorder?.amazon20DaysLastSale ?? 0);
            const snap = Number(item.b2cCount ?? 0);
            const dist = Number(item.totalQuantity ?? 0);
            const totalSale = fk20 + amz20 + snap + dist;
            const avgRounded = totalSale > 0 ? Math.round(totalSale / 20) : 0;
            const avg = totalSale > 0 ? (totalSale / 20).toFixed(2) : "-";
            const reorderQty = avgRounded * 40;
            const leadDemand = avgRounded * 20;
            const reorderDeficitRaw = reorderQty - totalStock;
            const reorderDeficit =
              reorderDeficitRaw < 0 ? 0 : reorderDeficitRaw;

            const finalReorderQtyRaw =
              leadDemand + reorderDeficitRaw - Number(reorder?.yetToCome ?? 0);
            const finalReorderQty =
              finalReorderQtyRaw < 0 ? 0 : finalReorderQtyRaw;

            return {
              No: index + 1,
              SKU: item.sku,
              Title: item.title,
              Medium: item.medium,
              Edition: item.edition,
              "Yet to Come": reorder?.yetToCome ?? "-",
              "Current Stock (P/W)": pw,
              "Amazon Stock": amazon,
              "Flipkart Stock": flipkart,
              "Total Stock": totalStock,
              "Min Stock (25d)": avgRounded * 25,
              "Max Stock (40d)": reorderQty,
              "Demand Rate": avgRounded,
              "Lead Time": 20,
              "FK 20d Sale": fk20,
              "Amazon 20d Sale": amz20,
              "Snapdeal 20d": snap,
              "Distributor 20d": dist,
              "Avg/Day": avg,
              "Max - Current": reorderDeficit,
              "Lead × Demand": leadDemand,
              "Reorder Qty": finalReorderQty,
            };
          })}
          filename="reorder_data.csv"
        >
          Export CSV
        </CSVLink>
      </div>

      <Modal
        dialogClassName="reorder-modal"
        show={showModal}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="reorder-modal-header">
          <Modal.Title>Save reorder stock details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px", backgroundColor: "#ffffff" }}>
          <Form>
            <Form.Group
              controlId="selectedSKU"
              style={{ marginBottom: "15px" }}
            >
              <Select
                classNamePrefix="reorder-select"
                options={skuData.map((item) => ({
                  value: item.sku,
                  label: `${item.sku} - ${item.title}`,
                }))}
                onChange={(selectedOption) =>
                  setFormValues((prev) => ({
                    ...prev,
                    selectedSKU: selectedOption ? selectedOption.value : "",
                  }))
                }
                placeholder="Search SKU..."
                isClearable
              />
            </Form.Group>

            {[
              { label: "Stock at Amazon Warehouse", name: "amazonWarehouse" },
              {
                label: "Stock at Flipkart Warehouse",
                name: "flipkartWarehouse",
              },
              {
                label: "Amazon 20 Days Last Sale",
                name: "amazon20DaysLastSale",
              },
              {
                label: "Flipkart 20 Days Last Sale",
                name: "flipkart20DaysLastSale",
              },
              { label: "Yet To Come", name: "yetToCome" },
            ].map((field, index) => (
              <Form.Group key={index} style={{ marginBottom: "15px" }}>
                <Form.Label style={{ fontWeight: "500", marginBottom: "5px" }}>
                  {field.label}
                </Form.Label>
                <Form.Control
                  type="number"
                  name={field.name}
                  value={formValues[field.name]}
                  onChange={handleChange}
                  style={{
                    padding: "8px",
                    borderRadius: "6px",
                    border: "1px solid #ced4da",
                    width: "100%",
                    maxWidth: "100%",
                  }}
                />
              </Form.Group>
            ))}
          </Form>
        </Modal.Body>

        <Modal.Footer
          style={{ backgroundColor: "#f8f9fa", borderTop: "1px solid #dee2e6" }}
        >
          <Button
            variant="secondary"
            onClick={handleClose}
            style={{
              padding: "6px 16px",
              borderRadius: "6px",
              fontWeight: "500",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            style={{
              padding: "6px 16px",
              borderRadius: "6px",
              fontWeight: "500",
            }}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
      <section className="reorder-table-card" aria-labelledby="reorder-table-title">
        <div className="reorder-table-heading">
          <div>
            <span className="reorder-eyebrow">RECOMMENDATIONS</span>
            <h2 id="reorder-table-title">Reorder planning table</h2>
            <p>Compare warehouse stock, recent sales, and suggested reorder quantities.</p>
          </div>
          <span className="reorder-table-count">{skuData.length.toLocaleString()} SKUs</span>
        </div>
        <div className="reorder-table-scroll">
        <Table striped bordered hover className="reorder-data-table">
          <thead>
            <tr>
              {[
                "Sr.No",
                "SKU",
                "Title",
                "Medium",
                "Edition",
                "Yet to Come",
                "Current Stock (P/W)",
                "Stock at amazon Warehouse",
                "Stock at fk Warehouse",
                "Total Stock (A+FK+P/W)",
                "Minimum stock threshold or Reorder Level (25 days)",
                "Maximum stock threshold (40 days)",
                "Demand Rate",
                "Lead Time",
                "Flipkart last 20 days sale",
                "Amazon last 20 days sale",
                "Snapdeal last 20 days sale",
                "Distributor last 20 days sale",
                "Avg/Day",
                "Max - Current",
                "Lead × Demand",
                "Reorder Qty",
              ].map((text, i) => (
                <th key={i}>
                  {text}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {skuData.length > 0 ? (
              skuData
                .filter(
                  (item) =>
                    (item.sku || "").toUpperCase().includes(searchTerm) ||
                    (item.title || "").toUpperCase().includes(searchTerm)
                )

                .map((item, index) => {
                  const reorder = getLatestReorderForSKU(item.sku);
                  const pw = getShelfInfoBySKU(item.sku).total;
                  const amazon = Number(reorder?.amazonWarehouse ?? 0);
                  const flipkart = Number(reorder?.flipkartWarehouse ?? 0);
                  const totalStock = (!isNaN(pw) ? pw : 0) + amazon + flipkart;

                  const fk20 = Number(reorder?.flipkart20DaysLastSale ?? 0);
                  const amz20 = Number(reorder?.amazon20DaysLastSale ?? 0);
                  const snap = Number(item.b2cCount ?? 0);
                  const dist = Number(item.totalQuantity ?? 0);
                  const totalSale = fk20 + amz20 + snap + dist;
                  const avgRounded =
                    totalSale > 0 ? Math.round(totalSale / 20) : 0;
                  const avg = totalSale > 0 ? (totalSale / 20).toFixed(2) : "-";
                  const reorderQty = avgRounded * 40;
                  const leadDemand = avgRounded * 20;
                  const reorderDeficitRaw = reorderQty - totalStock;
                  const reorderDeficit =
                    reorderDeficitRaw < 0 ? 0 : reorderDeficitRaw;

                  const finalReorderQtyRaw =
                    leadDemand +
                    reorderDeficitRaw -
                    Number(reorder?.yetToCome ?? 0);
                  const finalReorderQty =
                    finalReorderQtyRaw < 0 ? 0 : finalReorderQtyRaw;

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td style={tdStyle}>{item.sku}</td>
                      <td
                        style={{
                          maxWidth: "250px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                        }}
                        title={item.title}
                      >
                        {item.title}
                      </td>

                      <td style={tdStyle}>{item.medium}</td>
                      <td style={tdStyle}>{item.edition}</td>
                      <td style={tdStyle}>{reorder?.yetToCome ?? "-"}</td>
                      <td
                        style={tdStyle}
                        title={getShelfInfoBySKU(item.sku).tooltip}
                      >
                        <i
                          className="fa fa-eye"
                          style={{ color: "blue", marginRight: "5px" }}
                        ></i>
                        {pw}
                      </td>
                      <td style={tdStyle}>{amazon}</td>
                      <td style={tdStyle}>{flipkart}</td>
                      <td style={tdStyle}>{totalStock}</td>
                      <td style={tdStyle}>{avgRounded * 25}</td>
                      <td style={tdStyle}>{reorderQty}</td>
                      <td style={tdStyle}>{avgRounded}</td>
                      <td style={tdStyle}>20</td>
                      <td style={tdStyle}>{fk20}</td>
                      <td style={tdStyle}>{amz20}</td>
                      <td style={tdStyle}>{snap}</td>
                      <td style={tdStyle}>{dist}</td>
                      <td style={tdStyle}>{avg}</td>
                      <td style={tdStyle}>{reorderDeficit}</td>
                      <td style={tdStyle}>{leadDemand}</td>
                      <td style={tdStyle}>{finalReorderQty}</td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td
                  colSpan="22"
                  style={{
                    textAlign: "center",
                    padding: "10px",
                    fontSize: "14px",
                  }}
                >
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      </section>
      </main>
    </div>
  );
}

export default Reorder;
